import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function EditWordDialog({ word, open, onOpenChange, onSaved }) {
  const [translation, setTranslation] = useState("");
  const [transcription, setTranscription] = useState("");
  const [partOfSpeech, setPartOfSpeech] = useState("");
  const [definition, setDefinition] = useState("");
  const queryClient = useQueryClient();

  useEffect(() => {
    if (word) {
      setTranslation(word.translation || "");
      setTranscription(word.transcription || "");
      setPartOfSpeech(word.part_of_speech || "");
      setDefinition(word.definition || "");
    }
  }, [word]);

  const mutation = useMutation({
    mutationFn: () =>
      api.patch(`/words/${word.id}`, {
        translation,
        transcription,
        part_of_speech: partOfSpeech,
        definition,
      }),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: ["words"] });
      toast.success("Слово обновлено");
      onSaved?.(data);
      onOpenChange(false);
    },
    onError: () => toast.error("Не удалось обновить слово"),
  });

  if (!word) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-word-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Редактировать «{word.word}»</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">Перевод</label>
            <Input value={translation} onChange={(e) => setTranslation(e.target.value)} data-testid="edit-word-translation-input" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Транскрипция</label>
            <Input value={transcription} onChange={(e) => setTranscription(e.target.value)} data-testid="edit-word-transcription-input" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Часть речи</label>
            <Input value={partOfSpeech} onChange={(e) => setPartOfSpeech(e.target.value)} data-testid="edit-word-pos-input" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Определение</label>
            <Textarea value={definition} onChange={(e) => setDefinition(e.target.value)} rows={2} data-testid="edit-word-definition-input" />
          </div>
          <Button type="submit" className="w-full" disabled={mutation.isPending} data-testid="edit-word-submit-button">
            {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Сохранить"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
